// Type definition class
import { DataFormatterType } from './DataFormatterType';
import { DataFormatterOptions } from './DataFormatterType';

// Import each type of formatter supported
import { DataFormatNumber } from './formats/DataFormatNumber';
import { DataFormatText } from './formats/DataFormatText';
import { DataFormatCurrency } from './formats/DataFormatCurrency';
import { DataFormatWeight } from './formats/DataFormatWeight';
import { DataFormatLength } from './formats/DataFormatLength';
import { DataFormatDate } from './formats/DataFormatDate';
import { DataFormatDateTime } from './formats/DataFormatDateTime';
import { DataFormatTime } from './formats/DataFormatTime';
import { DataFormatBoolean } from './formats/DataFormatBoolean';
import { DataFormatMeasureWeight } from './formats/DataFormatMeasureWeight';
import { DataFormatMeasureLiquid } from './formats/DataFormatMeasureLiquid';
import { DataFormatMeasurePhysical } from './formats/DataFormatMeasurePhysical';
import { DataFormatDecimalPicogramsPerMilliliter } from './formats/DataFormatDecimalPicogramsPerMilliliter';
import { DataFormatDecimalKCellsPerMicroLiter } from './formats/DataFormatDecimalKCellsPerMicroLiter';
import { DataFormatDecimalMCellsPerMicroLiter } from './formats/DataFormatDecimalMCellsPerMicroLiter';
import { DataFormatDecimalMilligramsPerDeciliter } from './formats/DataFormatDecimalMilligramsPerDeciliter';
import { DataFormatDecimalMillionPerMicroLiter } from './formats/DataFormatDecimalMillionPerMicroLiter';
import { DataFormatDecimalMillimolesPerLitre } from './formats/DataFormatDecimalMillimolesPerLitre';
import { DataFormatDecimalOneCubicMicroMeter } from './formats/DataFormatDecimalOneCubicMicroMeter';
import { DataFormatDecimalPerDeciLiter } from './formats/DataFormatDecimalPerDeciLiter';
import { DataFormatDecimalPicograms } from './formats/DataFormatDecimalPicograms';
import { DataFormatDecimalThousandPerMicroLiter } from './formats/DataFormatDecimalThousandPerMicroLiter';
import { DataFormatPercent } from './formats/DataFormatPercent';
import { DataFormatDecimalGramsPerDeciliter } from './formats/DataFormatDecimalGramsPerDeciliter';
import { DataFormatDecimalMillimolesPerLiter } from './formats/DataFormatDecimalMillimolesPerLiter';
import { DataFormatDecimalInternationalUnitsPerLiter } from './formats/DataFormatDecimalInternationalUnitsPerLiter';
import { DataFormatDecimalGramsPerLiter } from './formats/DataFormatDecimalGramsPerLiter';
import { DataFormatDecimalNanogramsPerMilliliter } from './formats/DataFormatDecimalNanogramsPerMilliliter';
import { DataFormatDecimalMilligramsPerLiter } from './formats/DataFormatDecimalMilligramsPerLiter';
import { DataFormatDecimalMicrogramsPerMilliliter } from './formats/DataFormatDecimalMicrogramsPerMilliliter';
import { DataFormatDecimalMicromolar } from './formats/DataFormatDecimalMicromolar';
import { DataFormatDecimalGeometricMeanTiter } from './formats/DataFormatDecimalGeometricMeanTiter';
import { DataFormatDecimalMicroLiter } from './formats/DataFormatDecimalMicroLiter';
import { DataFormatDecimal } from './formats/DataFormatDecimal';
import { DataFormatDecimalMMHour } from './formats/DataFormatDecimalMMHour';
import { DataFormat2Logdilution } from './formats/DataFormat2Logdilution';
import { DataFormatDecimalUnitPerLiter } from './formats/DataFormatDecimalUnitPerLiter';
import { DataFormatDecimalMillimetersMinuteMeterSq } from './formats/DataFormatMillimetersMinuteMeterSq';
import { DataFormatYear } from './formats/DataFormatYear';

/**
 *  Provides access to all the registered data type formatters.
 *  @category Data Formatting
 */
export class DataFormatter {
    formats: any = { string: DataFormatterType };

    //
    //  function to register the data formatter class
    //
    //  @param [DataFormatterType] formattingClass
    //  @return null
    //
    register(formattingClass: any) {
        let name = formattingClass.getName();
        name = name.toLowerCase().trim();
        return (this.formats[name] = formattingClass);
    }

    /** Checks to see if a given type is registered */
    isTypeValid(dataType : string) : boolean
    {
        if (typeof(dataType) != "string") return false;

        let key = dataType.toLowerCase().trim();
        if (!this.formats[key]) {
            return false;
		}

		return true;
    }

    //
    //  function to get the data formatter from registered formatter
    //
    //  @param [String] dataTpe data type name of the desired formatter class
    //  @note fires error if formatter not found
    //  @return DataFormatterType
    //
    getFormatter(dataType: string): DataFormatterType {

        if (!dataType)
        {
            console.error("DataFormatter Critical Error in getFormatter, null or missing data type:  type=", dataType, " is not valid.");
        }

        let key = dataType.toString().toLowerCase().trim();
        if (!this.formats[key]) {
            // console.warn("DataFormatter Warning, type ", key, " not registered.");
            // console.log("Registered types:", this.formats);
            // throw new Error(`Invalid type: ${dataType}`);

            // return default text type until we have more written
            return this.formats['text'];
        }

        return this.formats[key];
    }

    /*
    Get the display units for a given formatter
    */
    displayUnits(dataType: string, options?: DataFormatterOptions) {
        const formatter = this.getFormatter(dataType);
        return formatter.getUnitText(options);
    }

    /**
     Format some data based on the type and
     return just the formatted value, not the style or other details.

     @param [String] dataType data type name for which formatter is running
     @param [Object] data data to be formatted
     @param [Object] options additional options to apply formatting
     @param [Object] Related object which the formatter is running

     @return [string] value formatted data using data type formatter
    */
    formatData(
        dataType: string,
        data: any,
        options?: DataFormatterOptions | string,
        refObj?: any,
    ): string {
        const formatter = this.getFormatter(dataType);
        if (!formatter) {
            return data;
        }
        return formatter.format(data, options, refObj);
    }

    /** Return the default alignment for data of this type */
    getAlign(dataType: string) {
        const formatter = this.getFormatter(dataType);
        if (!formatter) return 'left';
        return formatter.getAlign();
    }

    //
    //  Format some data based on the type and
    //  return just the formatted value.  This is specifically formatted for export.
    //
    //  @param [String] dataType data type name for which formatter is running
    //  @param [Object] data data to be formatted
    //  @param [Object] options additional options to apply formatting
    //  @param [Object] Related object which the formatter is running
    //
    //  @return [string] value formatted data using data type formatter
    //
    formatDataExport(
        dataType: string,
        data: any,
        options?: DataFormatterOptions | any,
        refObj?: any,
    ): string {
        const formatter = this.getFormatter(dataType);
        if (!formatter) {
            return data;
        }
        return formatter.formatExport(data, options, refObj);
    }

    //
    //  UnFormat some data based on the type and
    //  return just the unformatted value, not the style or other details.
    //
    //  @param [String] dataType data type name for which formatter is running
    //  @param [Object] data data to be formatted
    //  @param [Object] options additional options to apply formatting
    //  @param [Object] Related object which the formatter is running
    //
    //  @return [any] value unformatted data using data type formatter
    //
    unformatData(dataType: any, data: any, refObj?: any): any {
        const formatter = this.getFormatter(dataType);
        if (!formatter) {
            return data;
        }
        return formatter.unformat(data, refObj);
    }

    //
    //  Validate the data is of the correct type
    //  For example, if this is a number field and you pass text, it will return false
    //  If this is a date type then return the date field.
    //
    //  @param [String] dataType data type name for which formatter is running
    //  @param [Object] data data to be formatted
    //  @param [Object] options additional options to apply formatting
    //  @param [Object] Related object which the formatter is running
    //
    //  @return [any] value unformatted data using data type formatter
    //
    validateData(
        dataType: string,
        data: any,
        options?: DataFormatterOptions | any,
        refObj?: any,
    ): any {
        try {
            const formatter = this.getFormatter(dataType);
            return formatter.validate(data, options, refObj);
        } catch {
            return false;
        }
    }

    //
    //  constructor
    //  Start by registering all the known data types
    //
    constructor() {
        const formatTypes: any = {
            DataFormatNumber,
            DataFormatText,
            DataFormatCurrency,
            DataFormatWeight,
            DataFormatLength,
            DataFormatDate,
            DataFormatTime,
            DataFormatBoolean,
            DataFormatDateTime,
            DataFormatMeasurePhysical,
            DataFormatMeasureWeight,
            DataFormatMeasureLiquid,
            DataFormatDecimalPicogramsPerMilliliter,
            DataFormatDecimalKCellsPerMicroLiter,
            DataFormatDecimalMCellsPerMicroLiter,
            DataFormatDecimalMilligramsPerDeciliter,
            DataFormatDecimalMillionPerMicroLiter,
            DataFormatDecimalMillimolesPerLitre,
            DataFormatDecimalOneCubicMicroMeter,
            DataFormatDecimalPerDeciLiter,
            DataFormatDecimalPicograms,
            DataFormatDecimalThousandPerMicroLiter,
            DataFormatDecimalGramsPerDeciliter,
            DataFormatDecimalGramsPerLiter,
            DataFormatPercent,
            DataFormatDecimalMillimolesPerLiter,
            DataFormatDecimalInternationalUnitsPerLiter,
            DataFormatDecimalNanogramsPerMilliliter,
            DataFormatDecimalMilligramsPerLiter,
            DataFormatDecimalMicrogramsPerMilliliter,
            DataFormatDecimalMicromolar,
            DataFormatDecimalGeometricMeanTiter,
            DataFormatDecimalMicroLiter,
            DataFormatDecimalMMHour,
            DataFormatDecimalMillimetersMinuteMeterSq,
            DataFormatDecimal,
            DataFormat2Logdilution,
            DataFormatDecimalUnitPerLiter,
            DataFormatYear,
        };

        for (const key in formatTypes) {
            const type = formatTypes[key];
            this.register(new type());
        }
    }
}
